import { useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';

interface UploadState {
  file: File | null;
  fileName: string;
  fileSize: number;
  progress: number;
  status: 'idle' | 'validating' | 'uploading' | 'processing' | 'complete' | 'error';
  error: string | null;
  processingId: string | null;
}

export function useFileUpload() {
  const { token } = useAuth();
  const [state, setState] = useState<UploadState>({
    file: null,
    fileName: '',
    fileSize: 0,
    progress: 0,
    status: 'idle',
    error: null,
    processingId: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const maxFileSize = 50 * 1024 * 1024;

  const validateFile = useCallback((file: File): string | null => {
    if (!acceptedTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      return 'Invalid file type. Please upload a CSV or Excel file.';
    }
    if (file.size > maxFileSize) {
      return 'File too large. Maximum size is 50MB.';
    }
    return null;
  }, []);

  const handleFile = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        setState((prev) => ({ ...prev, error, status: 'error' }));
        return;
      }
      setState({
        file,
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        status: 'idle',
        error: null,
        processingId: null,
      });
    },
    [validateFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const upload = useCallback(async () => {
    if (!state.file) return null;
    setState((prev) => ({ ...prev, status: 'uploading', progress: 10 }));

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://claims-iq.com/api';
      const formData = new FormData();
      formData.append('file', state.file);

      setState((prev) => ({ ...prev, progress: 30 }));

      const res = await fetch(`${baseUrl}/upload/validated`, {
        method: 'POST',
        headers: {
        Authorization: 'Bearer ' + (Object.keys(localStorage).filter(k => k.includes('idToken')).map(k => localStorage.getItem(k))[0] || token),
        },
        body: formData,
      });

      setState((prev) => ({ ...prev, progress: 70 }));

      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`Upload failed (${res.status}): ${errBody}`);
      }

      const data = await res.json();
      const processingId = data.uploadId || data.processingId || data.batchId || data.id || null;

      setState((prev) => ({
        ...prev,
        status: 'complete',
        progress: 100,
        processingId,
      }));

      // Store processingId in sessionStorage so ProcessingJobs can find it
      if (processingId) {
        const existing = JSON.parse(localStorage.getItem('claimsiq_jobs') || '[]');
        existing.push({
          processingId,
          fileName: state.file.name,
          fileSize: state.file.size,
          startTime: new Date().toISOString(),
          status: 'processing',
        });
        localStorage.setItem('claimsiq_jobs', JSON.stringify(existing));
      }

      return processingId;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setState((prev) => ({ ...prev, status: 'error', error: message, progress: 0 }));
      return null;
    }
  }, [state.file, token]);

  const reset = useCallback(() => {
    setState({ file: null, fileName: '', fileSize: 0, progress: 0, status: 'idle', error: null, processingId: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return {
    ...state,
    fileInputRef,
    handleDrop,
    handleInputChange,
    handleFile,
    upload,
    reset,
  };
}